/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { Injectable } from '@angular/core';
import { HttpRequestService } from '../../../_services/http-request.service';
import { ActivatedRoute } from '@angular/router';
import {map, switchMap, Observable, of } from 'rxjs';
import { NgEmailPassAuthProviderConfig } from './email-pass-auth.options';
import { NbAuthResult } from '../services/auth.service';
import { NbAbstractAuthProvider } from './abstract-auth.provider';
import { getDeepFromObject } from '../helpers';
import { environment } from './../../../../environments/environment';
import { NbAuthSimpleToken } from '../services/token.service';
import { JwtHelperService } from "@auth0/angular-jwt";
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
/**
 * The most common authentication provider for email/password strategy.
 *
 *
 * @example
 *
 * Default settings object:
 *
 * ```
 * {
 *  baseEndpoint: '',
 *  login: {
 *    alwaysFail: false,
 *    rememberMe: true,
 *    endpoint: '/api/auth/login',
 *    method: 'post',
 *    redirect: {
 *      success: '/',
 *      failure: null,
 *    },
 *    defaultErrors: ['Login/Email combination is not correct, please try again.'],
 *    defaultMessages: ['You have been successfully logged in.'],
 *  },
 *  register: {
 *    alwaysFail: false,
 *    rememberMe: true,
 *    endpoint: '/api/auth/register',
 *    method: 'post',
 *    redirect: {
 *      success: '/',
 *      failure: null,
 *    },
 *    defaultErrors: ['Something went wrong, please try again.'],
 *    defaultMessages: ['You have been successfully registered.'],
 *  },
 *  logout: {
 *    alwaysFail: false,
 *    endpoint: '/api/auth/logout',
 *    method: 'delete',
 *    redirect: {
 *      success: '/',
 *      failure: null,
 *    },
 *    defaultErrors: ['Something went wrong, please try again.'],
 *    defaultMessages: ['You have been successfully logged out.'],
 *  },
 *  requestPass: {
 *    endpoint: '/api/auth/request-pass',
 *    method: 'post',
 *    redirect: {
 *      success: '/',
 *      failure: null,
 *    },
 *    defaultErrors: ['Something went wrong, please try again.'],
 *    defaultMessages: ['Reset password instructions have been sent to your email.'],
 *  },
 *  resetPass: {
 *    endpoint: '/api/auth/reset-pass',
 *    method: 'put',
 *    redirect: {
 *      success: '/',
 *      failure: null,
 *    },
 *    resetPasswordTokenKey: 'reset_password_token',
 *    defaultErrors: ['Something went wrong, please try again.'],
 *    defaultMessages: ['Your password has been successfully changed.'],
 *  },
 *  token: {
 *    key: 'data.token',
 *    getter: (module: string, res: HttpResponse<Object>) => getDeepFromObject(res.body,
 *      this.getConfigValue('token.key')),
 *  },
 *  errors: {
 *    key: 'data.errors',
 *    getter: (module: string, res: HttpErrorResponse) => getDeepFromObject(res.error,
 *      this.getConfigValue('errors.key'),
 *      this.getConfigValue(`${module}.defaultErrors`)),
 *  },
 *  messages: {
 *    key: 'data.messages',
 *    getter: (module: string, res: HttpResponse<Object>) => getDeepFromObject(res.body,
 *      this.getConfigValue('messages.key'),
 *      this.getConfigValue(`${module}.defaultMessages`)),
 *  },
 *}
 *
 * // Note, there is no need to copy over the whole object to change the settings you need.
 * // Also, this.getConfigValue call won't work outside ofthe default config declaration
 * // (which is inside of the `NbEmailPassAuthProvider` class), so you have to replace it with a custom helper function
 * // if you need it.
 * ```
 */

@Injectable()
export class NbEmailPassAuthProvider extends NbAbstractAuthProvider {
  private jwtHelper: JwtHelperService = new JwtHelperService();

  protected defaultConfig: NgEmailPassAuthProviderConfig = {
    baseEndpoint: environment.BeApiOperational.url,
    login: {
      alwaysFail: false,
      rememberMe: true,
      endpoint: '/api/authenticate',
      method: 'post',
      redirect: {
        success: '/home',
        failure: null,
      },
      defaultErrors: [`L'identifiant ou le mot de passe est incorrect.`],
      defaultMessages: ['Authentification réussie.'],
    },
    refresh: {
      alwaysFail: false,
      rememberMe: true,
      endpoint: '/api/refresh',
      method: 'post',
      redirect: {
        success: '/home',
        failure: null,
      },
      defaultErrors: [`L'identifiant ou le mot de passe est incorrect.`],
      defaultMessages: ['Authentification réussie.'],
    },
    register: {
      alwaysFail: false,
      rememberMe: true,
      endpoint: '/api/auth/register',
      method: 'post',
      redirect: {
        success: '/',
        failure: null,
      },
      defaultErrors: ['Something went wrong, please try again.'],
      defaultMessages: ['You have been successfully registered.'],
    },
    logout: {
      alwaysFail: false,
      endpoint: '/api/auth/logout',
      method: 'delete',
      redirect: {
        success: '/',
        failure: null,
      },
      defaultErrors: ['Something went wrong, please try again.'],
      defaultMessages: ['You have been successfully logged out.'],
    },
    requestPass: {
      endpoint: '/api/auth/request-pass',
      method: 'post',
      redirect: {
        success: '/',
        failure: null,
      },
      defaultErrors: ['Something went wrong, please try again.'],
      defaultMessages: ['Reset password instructions have been sent to your email.'],
    },
    resetPass: {
      endpoint: '/api/auth/reset-pass',
      method: 'put',
      redirect: {
        success: '/',
        failure: null,
      },
      resetPasswordTokenKey: 'reset_password_token',
      defaultErrors: ['Something went wrong, please try again.'],
      defaultMessages: ['Your password has been successfully changed.'],
    },
    token: {
      key: 'data.token',
      getter: (module: string, res: HttpResponse<Object>) => getDeepFromObject(res.body,
        this.getConfigValue('token.key')),
    },
    refreshToken: {
      key: 'Refresh_Token',
      getter: (module: string, res: HttpResponse<Object>) => getDeepFromObject(res.body,
        this.getConfigValue('refreshToken.key')),
    },
    newToken: {
      key: 'Token',
      getter: (module: string, res: HttpResponse<Object>) => getDeepFromObject(res.body,
        this.getConfigValue('newToken.key')),
    },
    errors: {
      key: 'data.errors',
      getter: (module: string, res: HttpErrorResponse) => getDeepFromObject(res.error,
        this.getConfigValue('errors.key'),
        this.getConfigValue(`${module}.defaultErrors`)),
    },
    messages: {
      key: 'data.messages',
      getter: (module: string, res: HttpResponse<Object>) => getDeepFromObject(res.body,
        this.getConfigValue('messages.key'),
        this.getConfigValue(`${module}.defaultMessages`)),
    },
  };

  constructor(protected httpRequestService: HttpRequestService, private route: ActivatedRoute) {
    super();
  }


  authenticate(data?: any): Observable<NbAuthResult> {
    const url = this.getActionEndpoint('login');
    return this.httpRequestService.post(url, data, 'authenticate')
      .pipe(
        map((res) => {
          if (this.getConfigValue('login.alwaysFail')) {
            throw this.createFailResponse(data);
          }
          return res;
        })
      )
      .pipe(
        map((res) => {
          return new NbAuthResult(
            true,
            res,
            this.getConfigValue('login.redirect.success'),
            [],
            this.getConfigValue('messages.getter')('login', res),
            this.getConfigValue('token.getter')('login', res));
        })
      )
      .pipe(
        catchError((res) => {
          let errors = [];
          if (res instanceof HttpErrorResponse) {
            errors = this.getConfigValue('errors.getter')('login', res);
          } else {
            errors.push('Something went wrong.');
          }

          return of(
            new NbAuthResult(
              false,
              res,
              this.getConfigValue('login.redirect.failure'),
              errors,
            ));
        })
      );
  }

  refresh(token: string, refresh_token: string): Observable<NbAuthResult> {

      if(this.jwtHelper.isTokenExpired(token)) {
        const method = this.getConfigValue('refresh.method');
        const url = this.getActionEndpoint('refresh') + "?refresh_token=" + refresh_token;
        return this.httpRequestService.post(url, { body: refresh_token, observe: 'response' }, 'refresh')
          .pipe(
            map((res) => {
              if (this.getConfigValue('login.alwaysFail')) {
                throw this.createFailResponse(refresh_token);
              }

              return res;
            })
          )
          .pipe(
              map((res) => {
                let authSimple = new NbAuthSimpleToken();
                authSimple.setRefreshTokenValue(this.getConfigValue('refreshToken.getter')('refresh', res));
                authSimple.setValue(this.getConfigValue('newToken.getter')('refresh', res));

                return new NbAuthResult(
                  true,
                  res,
                  this.getConfigValue('refresh.redirect.success'),
                  [],
                  this.getConfigValue('messages.getter')('refresh', res),
                  authSimple
                );
            })
          )
          .pipe(
            catchError((res) => {
              let errors = [];
              if (res instanceof HttpErrorResponse) {
                errors = this.getConfigValue('errors.getter')('refresh', res);
              } else {
                errors.push('Something went wrong.');
              }

              return of(
                new NbAuthResult(
                  false,
                  res,
                  this.getConfigValue('login.redirect.failure'),
                  errors,
                ));
            })
          );
      }
      else
      {
        let authSimple = new NbAuthSimpleToken();
        authSimple.setRefreshTokenValue(refresh_token);
        authSimple.setValue(token);

        return of( new NbAuthResult(
          true,
          null,
          this.getConfigValue('refresh.redirect.success'),
          [],
          null,
          authSimple
        ));
      }
  }
  register(data?: any): Observable<NbAuthResult> {
    const method = this.getConfigValue('register.method');
    const url = this.getActionEndpoint('register');
    return this.httpRequestService.request(method, url, { body: data, observe: 'response' })
      .pipe(
        map((res) => {
          if (this.getConfigValue('register.alwaysFail')) {
            throw this.createFailResponse(data);
          }
          return res;
        })
      )
      .pipe(
        map((res) => {
        return new NbAuthResult(
          true,
          res,
          this.getConfigValue('register.redirect.success'),
          [],
          this.getConfigValue('messages.getter')('register', res),
          this.getConfigValue('token.getter')('register', res));
      })
      )
      .pipe(
        catchError((res) => {
          let errors = [];
          if (res instanceof HttpErrorResponse) {
            errors = this.getConfigValue('errors.getter')('register', res);
          } else {
            errors.push('Something went wrong.');
          }

          return of(
            new NbAuthResult(
              false,
              res,
              this.getConfigValue('register.redirect.failure'),
              errors,
            ));
        })
      );
  }

  requestPassword(data?: any): Observable<NbAuthResult> {
    const method = this.getConfigValue('requestPass.method');
    const url = this.getActionEndpoint('requestPass');
    return this.httpRequestService.request(method, url, { body: data, observe: 'response' })
      .pipe(
        map((res) => {
          if (this.getConfigValue('requestPass.alwaysFail')) {
            throw this.createFailResponse();
          }

          return res;
        })
      )
      .pipe(
        map((res) => {
          return new NbAuthResult(
            true,
            res,
            this.getConfigValue('requestPass.redirect.success'),
            [],
            this.getConfigValue('messages.getter')('requestPass', res));
        })
      )
      .pipe(
        catchError((res) => {
          let errors = [];
          if (res instanceof HttpErrorResponse) {
            errors = this.getConfigValue('errors.getter')('requestPass', res);
          } else {
            errors.push('Something went wrong.');
          }

          return of(
            new NbAuthResult(
              false,
              res,
              this.getConfigValue('requestPass.redirect.failure'),
              errors,
            ));
        })
      );
  }

  resetPassword(data: any = {}): Observable<NbAuthResult> {
    const tokenKey = this.getConfigValue('resetPass.resetPasswordTokenKey');
    data[tokenKey] = this.route.snapshot.queryParams[tokenKey];

    const method = this.getConfigValue('resetPass.method');
    const url = this.getActionEndpoint('resetPass');
    return this.httpRequestService.post(url, { body: data, observe: 'response' }, 'resetPassword')
      .pipe(
        map((res) => {
          if (this.getConfigValue('resetPass.alwaysFail')) {
            throw this.createFailResponse();
          }

          return res;
        })
      )
      .pipe(
        map((res) => {
          return new NbAuthResult(
            true,
            res,
            this.getConfigValue('resetPass.redirect.success'),
            [],
            this.getConfigValue('messages.getter')('resetPass', res));
        })
      )
      .pipe(
        catchError((res) => {
        let errors = [];
        if (res instanceof HttpErrorResponse) {
          errors = this.getConfigValue('errors.getter')('resetPass', res);
        } else {
          errors.push('Something went wrong.');
        }

        return of(
          new NbAuthResult(
            false,
            res,
            this.getConfigValue('resetPass.redirect.failure'),
            errors,
          ));
      })
      );
  }

  logout(): Observable<NbAuthResult> {

    const method = this.getConfigValue('logout.method');
    const url = this.getActionEndpoint('logout');
    return of({})
      .pipe(
        switchMap((res: any) => {
          if (!url) {
            return of(res);
          }
          return this.httpRequestService.post(url, { observe: 'response' }, 'logout');
        })
      )
      .pipe(
        map((res) => {
          if (this.getConfigValue('logout.alwaysFail')) {
            throw this.createFailResponse();
          }

          return res;
        })
      )
      .pipe(
        map((res) => {
          return new NbAuthResult(
            true,
            res,
            this.getConfigValue('logout.redirect.success'),
            [],
            this.getConfigValue('messages.getter')('logout', res));
        })
      )
      .pipe(
        catchError((res) => {
          let errors = [];
          if (res instanceof HttpErrorResponse) {
            errors = this.getConfigValue('errors.getter')('logout', res);
          } else {
            errors.push('Something went wrong.');
          }

          return of(
            new NbAuthResult(
              false,
              res,
              this.getConfigValue('logout.redirect.failure'),
              errors,
            ));
        })
      );
  }

  protected getActionEndpoint(action: string): string {
    const actionEndpoint: string = this.getConfigValue(`${action}.endpoint`);
    const baseEndpoint: string = this.getConfigValue('baseEndpoint');
    return baseEndpoint + actionEndpoint;
  }
}
